/**
 * The office's characters, by name.
 *
 * A team member's `avatar` is an index into `assets/characters/char_<n>.png`.
 * With six sheets a row of unnamed sprites was a fine picker; with thirty and
 * growing it is a wall, and "Character 17" tells nobody it is a chef. This is
 * the list the pickers read: what each one is, which shelf it sits on, and
 * which roles it suits, so a reviewer is offered a librarian before a Bigfoot.
 *
 * Indices are forever — saved teams refer to them — so entries are only ever
 * added. A sheet with no entry still works everywhere (`characterInfo()`
 * falls back to "Character N" under Other); `webview-ui/test/
 * characterCatalog.test.ts` fails while one is missing, naming the line to
 * add. No DOM, no sprite data: the tests and the models can import it.
 */

export type CharacterGroup = 'office' | 'uniform' | 'profession' | 'fantastic' | 'other';

export const CHARACTER_GROUPS: readonly { id: CharacterGroup; label: string }[] = [
  { id: 'office', label: 'Office' },
  { id: 'uniform', label: 'In uniform' },
  { id: 'profession', label: 'Professions' },
  { id: 'fantastic', label: 'Fantastic' },
  { id: 'other', label: 'Other' },
];

export interface CharacterInfo {
  index: number;
  name: string;
  group: CharacterGroup;
  /** Role ids (`src/coordination/roles.ts`) this one is offered for first. */
  roles?: readonly string[];
  /** Extra words the picker's search matches. */
  keywords?: readonly string[];
}

const entry = (index: number, name: string, group: CharacterGroup, roles?: string[], keywords?: string[]): CharacterInfo => ({ index, name, group, roles, keywords });

// Names say what a character is and, where two share a costume, what you can *see* that tells them
// apart — a ponytail, a visor colour, a bow tie — the way "Racing driver (red)" always did. Never a
// gender: an agent has none, and a picker that files its reviewers under "male" and "female" is
// making a statement nobody asked it to make.
export const CHARACTER_CATALOG: readonly CharacterInfo[] = [
  entry(0, 'Blue suit', 'office', ['ops-manager', 'planner'], ['manager', 'lead', 'tie']),
  entry(1, 'Black dress', 'office', ['implementer', 'generalist'], ['blonde']),
  entry(2, 'Orange jacket', 'office', ['implementer', 'generalist'], ['developer']),
  entry(3, 'Silver hair', 'office', ['reviewer'], ['senior']),
  entry(4, 'Spiky hair', 'office', ['diagrammer', 'scribe', 'generalist'], ['writer', 'artist', 'diagrams']),
  entry(5, 'Red top', 'office', ['researcher', 'diagrammer'], ['analyst', 'diagrams']),
  entry(6, 'Police officer', 'uniform', ['reviewer', 'debugger'], ['cop', 'security', 'guard']),
  entry(7, 'Police officer (ponytail)', 'uniform', ['reviewer', 'debugger'], ['cop', 'security', 'guard']),
  entry(8, 'Construction worker', 'uniform', ['implementer'], ['builder', 'hard hat', 'engineer']),
  entry(9, 'Construction worker (ponytail)', 'uniform', ['implementer'], ['builder', 'hard hat', 'engineer']),
  entry(10, 'Alien (green)', 'fantastic', undefined, ['space', 'green']),
  entry(11, 'Alien (violet crest)', 'fantastic', undefined, ['space', 'green']),
  entry(12, 'Robot (cyan visor)', 'fantastic', ['implementer'], ['bot', 'android', 'machine']),
  entry(13, 'Robot (violet visor)', 'fantastic', ['implementer'], ['bot', 'android', 'machine']),
  entry(14, 'Pilot', 'uniform', ['ops-manager', 'planner', 'integrator'], ['captain', 'airline']),
  entry(15, 'Pilot (ponytail)', 'uniform', ['ops-manager', 'planner', 'integrator'], ['captain', 'airline']),
  entry(16, 'Bigfoot (dark brown)', 'fantastic', undefined, ['sasquatch', 'yeti']),
  entry(17, 'Bigfoot (auburn)', 'fantastic', undefined, ['sasquatch', 'yeti']),
  entry(18, 'Chef (moustache)', 'profession', ['integrator'], ['cook', 'kitchen']),
  entry(19, 'Chef (brown hair)', 'profession', ['integrator'], ['cook', 'kitchen']),
  entry(20, 'Scientist (silver hair)', 'profession', ['researcher'], ['lab', 'research', 'glasses']),
  entry(21, 'Scientist (hair bun)', 'profession', ['researcher'], ['lab', 'research', 'glasses']),
  entry(22, 'Racing driver (red)', 'uniform', ['implementer'], ['helmet', 'fast', 'race']),
  entry(23, 'Racing driver (purple)', 'uniform', ['implementer'], ['helmet', 'fast', 'race']),
  entry(24, 'Bee costume', 'fantastic', ['debugger'], ['bug', 'debug']),
  entry(25, 'Bee costume (brown fringe)', 'fantastic', ['debugger'], ['bug', 'debug']),
  entry(26, 'Librarian (bow tie)', 'profession', ['scribe', 'reviewer'], ['docs', 'books', 'writer', 'glasses']),
  entry(27, 'Librarian (silver bun)', 'profession', ['scribe', 'reviewer'], ['docs', 'books', 'writer', 'glasses']),
  entry(28, 'Cave person (beard)', 'fantastic', undefined, ['prehistoric', 'stone age', 'legacy']),
  entry(29, 'Cave person (bone hairpin)', 'fantastic', undefined, ['prehistoric', 'stone age', 'legacy']),
  entry(30, 'Black suit', 'office', ['ops-manager', 'planner'], ['formal', 'executive', 'silver hair']),
  entry(31, 'Gym enthusiast', 'profession', undefined, ['gym', 'fitness', 'trainer', 'workout']),
  entry(32, 'Yoga / Pilates instructor', 'profession', undefined, ['yoga', 'pilates', 'fitness', 'instructor']),
  entry(33, 'Symphony conductor (silver hair)', 'profession', ['ops-manager', 'integrator'], ['orchestra', 'music']),
  entry(34, 'Symphony conductor (hair bun)', 'profession', ['ops-manager', 'integrator'], ['orchestra', 'music']),
  entry(35, 'Train conductor (moustache)', 'uniform', ['ops-manager', 'planner'], ['railway', 'railroad', 'train']),
  entry(36, 'Train conductor (hair tied back)', 'uniform', ['ops-manager', 'planner'], ['railway', 'railroad', 'train']),
  entry(37, 'Programmer (black hair)', 'profession', ['implementer', 'debugger'], ['IT', 'nerd', 'developer', 'coder', 'glasses']),
  entry(38, 'Programmer (auburn bob)', 'profession', ['implementer', 'debugger'], ['IT', 'nerd', 'developer', 'coder', 'glasses']),
  entry(39, 'Killer whale costume', 'fantastic', undefined, ['orca', 'whale', 'costume', 'human', 'mascot', 'ocean']),
  entry(40, 'Killer whale costume (auburn hair)', 'fantastic', undefined, ['orca', 'whale', 'costume', 'human', 'mascot', 'ocean', 'auburn']),
];

const BY_INDEX = new Map(CHARACTER_CATALOG.map((c) => [c.index, c]));

/** What is known about a sheet; a name of last resort for one nobody has described yet. */
export function characterInfo(index: number): CharacterInfo {
  return BY_INDEX.get(index) ?? { index, name: `Character ${index + 1}`, group: 'other' };
}

/** Every loaded sheet, catalogued or not, in index order. */
export function listCharacters(count: number): CharacterInfo[] {
  return Array.from({ length: Math.max(0, count) }, (_, i) => characterInfo(i));
}

/** Every word of the query somewhere in the name, the shelf or the keywords. */
export function searchCharacters(characters: readonly CharacterInfo[], query: string): CharacterInfo[] {
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return [...characters];
  return characters.filter((c) => {
    const hay = `${c.name} ${CHARACTER_GROUPS.find((g) => g.id === c.group)?.label ?? ''} ${(c.keywords ?? []).join(' ')}`.toLowerCase();
    return words.every((w) => hay.includes(w));
  });
}

/** The picker's shelves, in shelf order, empty ones left out. */
export function groupCharacters(characters: readonly CharacterInfo[]): { id: CharacterGroup; label: string; characters: CharacterInfo[] }[] {
  return CHARACTER_GROUPS.map((g) => ({ ...g, characters: characters.filter((c) => c.group === g.id) })).filter((g) => g.characters.length > 0);
}

const KNOWN_ROLES = [...new Set(CHARACTER_CATALOG.flatMap((c) => c.roles ?? []))];

/**
 * The role id behind what a member's role says. The wizard has the id; the
 * editor only has the words someone typed ("Senior reviewer", "Ops Manager"),
 * so an id is recognised inside them.
 */
export function roleKey(role: string | undefined): string | undefined {
  const text = role?.trim().toLowerCase().replace(/[\s_]+/g, '-');
  if (!text) return undefined;
  return KNOWN_ROLES.find((id) => text === id) ?? KNOWN_ROLES.find((id) => text.includes(id)) ?? KNOWN_ROLES.find((id) => text.includes(id.split('-')[0]));
}

/** The ones offered first for a role — an id, or the words of one. */
export function charactersForRole(characters: readonly CharacterInfo[], role: string | undefined): CharacterInfo[] {
  const key = roleKey(role);
  if (!key) return [];
  return characters.filter((c) => c.roles?.includes(key));
}

export interface PickCharacterOptions {
  /** How many sheets are loaded. */
  count: number;
  /** Characters already taken — in this roster and, for a first member, across the other teams. */
  used?: readonly number[];
  role?: string;
  /** Injected so a test can say which one comes out. */
  random?: () => number;
}

/**
 * A character for a new member: one nobody has yet, from the ones that suit
 * the role when there is a role. It used to be `index % 10`, which gave every
 * new specialist the same face and never reached a sheet past the tenth.
 * When everything is taken, the least used.
 */
export function pickCharacter({ count, used = [], role, random = Math.random }: PickCharacterOptions): number {
  if (count <= 0) return 0;
  const all = listCharacters(count);
  const uses = new Map<number, number>();
  for (const u of used) uses.set(u, (uses.get(u) ?? 0) + 1);
  const fewest = Math.min(...all.map((c) => uses.get(c.index) ?? 0));
  const free = all.filter((c) => (uses.get(c.index) ?? 0) === fewest);
  const suited = charactersForRole(free, role);
  const pool = suited.length ? suited : free;
  return pool[Math.min(pool.length - 1, Math.floor(random() * pool.length))].index;
}
